import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

@Directive({ selector: '[aavDialogActions]' })
export class DialogActionsDirective {}

let nextId = 0;

const HEADER_CLASSES =
  'relative shrink-0 bg-aav-primary px-[1.5rem] py-[0.875rem] text-aav-on-primary';

const HEADING_CLASSES = 'm-0 pr-[2.5rem] text-[15px] font-semibold leading-tight';

const SUBHEADING_CLASSES = 'mb-0 mt-[2px] text-xs opacity-90';

const CLOSE_CLASSES =
  'absolute right-[0.75rem] top-[0.75rem] flex h-8 w-8 items-center justify-center ' +
  'border-0 bg-transparent p-0 text-aav-on-primary cursor-pointer leading-none';

const BODY_CLASSES = 'aav-dialog-scroll flex-1 min-h-0 overflow-y-auto';

const FOOTER_CLASSES =
  'flex flex-none justify-end gap-3 border-b-0 border-l-0 border-r-0 border-t ' +
  'border-solid border-aav-neutral-200 px-[1.5rem] py-[1rem]';

@Component({
  selector: 'aav-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'aav-scope flex flex-1 flex-col min-h-0 bg-aav-surface' },
  styles: [
    `
      .aav-dialog-scroll {
        scrollbar-width: thin;
        scrollbar-color: rgb(var(--aav-outline-variant)) transparent;
      }

      .aav-dialog-scroll::-webkit-scrollbar {
        width: 6px;
      }

      .aav-dialog-scroll::-webkit-scrollbar-track {
        background: transparent;
      }

      .aav-dialog-scroll::-webkit-scrollbar-thumb {
        border-radius: 3px;
        background-color: rgb(var(--aav-outline-variant));
      }

      .aav-dialog-scroll::-webkit-scrollbar-thumb:hover {
        background-color: rgb(var(--aav-stone-400));
      }
    `,
  ],
  template: `
    <div [class]="headerClasses">
      <h2 [id]="headingId" [class]="headingClasses">{{ heading }}</h2>

      <p *ngIf="subheading" [class]="subheadingClasses" data-info="dialog-subheading">
        {{ subheading }}
      </p>

      <button
        *ngIf="dismissible"
        type="button"
        data-action="close"
        [class]="closeClasses"
        [attr.aria-label]="closeLabel || null"
        (click)="dismissed.emit()"
      >
        <aav-icon name="close" [size]="20"></aav-icon>
      </button>
    </div>

    <div [class]="bodyClasses">
      <ng-content></ng-content>
    </div>

    <div *ngIf="actions" [class]="footerClasses">
      <ng-content select="[aavDialogActions]"></ng-content>
    </div>
  `,
})
export class DialogComponent {
  @Input() heading = '';
  @Input() subheading = '';
  @Input() closeLabel = '';
  @Input() dismissible = true;

  @Output() dismissed = new EventEmitter<void>();

  @ContentChild(DialogActionsDirective, { static: true }) actions?: DialogActionsDirective;

  readonly headingId = `aav-dialog-${nextId++}`;
  readonly headerClasses = HEADER_CLASSES;
  readonly headingClasses = HEADING_CLASSES;
  readonly subheadingClasses = SUBHEADING_CLASSES;
  readonly closeClasses = CLOSE_CLASSES;
  readonly bodyClasses = BODY_CLASSES;
  readonly footerClasses = FOOTER_CLASSES;
}
